import { CloseOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Download, Barcode, Package, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";
import sampleFileCustomer from "../../data/sample_file_customer.xlsx"
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints"
import { toast } from "react-toastify";
import { getCustomers } from "../../API  Functions/SessionHistoryFunctions"
import { setDashData } from "../../Redux/Action/DashData"
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../loading"


const T = {
    accent: "#1b4597",
    accentDark: "#153880",
    accentBg: "#eff4ff",
    accentBd: "#b3c8f5",
    border: "#e2e8f0",
    muted: "#64748b",
    surface: "#f8fafc",
    text: "#0f172a",
    sub: "#475569",
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

.im * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; margin: 0; padding: 0; }

/* Overlay */
.im-ov {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(15,23,42,0.6);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}

/* Modal shell */
.im-box {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 32px 80px rgba(15,23,42,0.22);
  width: min(760px, 94vw);
  max-height: 94vh;
  display: flex; flex-direction: column;
  overflow: hidden;
}

/* Header */
.im-hd {
  padding: 16px 22px;
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid ${T.border};
  flex-shrink: 0;
}
.im-hd-title { font-size: 16px; font-weight: 700; color: ${T.text}; letter-spacing: -0.2px; }
.im-hd-close {
  width: 28px; height: 28px; border-radius: 7px;
  border: 1px solid ${T.border}; background: transparent;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: ${T.muted}; transition: all .15s;
}
.im-hd-close:hover { background: ${T.surface}; color: ${T.text}; }

/* Tabs */
.im-tabs {
  display: flex; gap: 6px;
  padding: 14px 22px 0;
  flex-shrink: 0;
}
.im-tab {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 10px; border-radius: 10px;
  border: 1.5px solid ${T.border};
  background: ${T.surface};
  font-size: 13px; font-weight: 600; color: ${T.muted};
  cursor: pointer; transition: all .18s;
}
.im-tab:hover:not(.im-tab-on) { border-color: #94a3b8; color: ${T.sub}; background: #f1f5f9; }
.im-tab-on {
  border-color: ${T.accent}; background: ${T.accentBg}; color: ${T.accentDark};
  box-shadow: 0 0 0 3px ${T.accentBd}30;
}

/* Scrollable body */
.im-body {
  padding: 14px 22px 4px;
  overflow-y: auto; flex: 1;
  display: flex; flex-direction: column; gap: 10px;
}

/* Info chip */
.im-chip {
  display: flex; align-items: center; gap: 7px;
  background: ${T.accentBg}; border: 1px solid ${T.accentBd};
  border-radius: 8px; padding: 7px 11px;
  font-size: 12px; color: ${T.accentDark};
}

/* Section label */
.im-lbl {
  font-size: 10.5px; font-weight: 700; letter-spacing: .7px;
  text-transform: uppercase; color: ${T.muted};
  margin-bottom: 6px;
}

/* Option grids */
.im-g3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 6px; }
.im-g2 { display: grid; grid-template-columns: repeat(2,1fr); gap: 6px; }

/* Option pill */
.im-opt {
  display: flex; align-items: center; gap: 7px;
  padding: 8px 10px; border-radius: 9px;
  border: 1.5px solid ${T.border}; background: ${T.surface};
  font-size: 12.5px; font-weight: 500; color: ${T.sub};
  cursor: pointer; transition: all .15s; user-select: none;
}
.im-opt:hover { border-color: #94a3b8; background: #f1f5f9; }
.im-opt-on { border-color: ${T.accent}; background: ${T.accentBg}; color: ${T.accentDark}; }

/* Radio dot */
.im-radio {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: all .15s;
}
.im-opt-on .im-radio { border-color: ${T.accent}; background: ${T.accent}; }
.im-rdot { width: 5px; height: 5px; border-radius: 50%; background: #fff; }

/* Checkbox */
.im-chk {
  width: 14px; height: 14px; border-radius: 4px;
  border: 2px solid #cbd5e1;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: all .15s; font-size: 8px; color: #fff;
}
.im-opt-on .im-chk { border-color: ${T.accent}; background: ${T.accent}; }

/* Divider */
.im-div { height: 1px; background: ${T.border}; }

/* Upload row */
.im-up {
  display: flex; align-items: center; gap: 11px;
  padding: 11px 13px; border-radius: 10px;
  border: 1.5px dashed #cbd5e1; background: ${T.surface};
  transition: border-color .15s;
}
.im-up:hover { border-color: ${T.accent}; }
.im-up-ico {
  width: 36px; height: 36px; border-radius: 8px;
  background: ${T.accentBg};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.im-up-info { flex: 1; min-width: 0; }
.im-up-name { font-size: 13px; font-weight: 600; color: ${T.text}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.im-up-sub { font-size: 11.5px; color: ${T.muted}; margin-top: 1px; }
.im-up-btn {
  padding: 7px 13px; border-radius: 7px;
  background: ${T.text}; color: #fff; border: none;
  font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: background .15s;
}
.im-up-btn:hover { background: #1e293b; }

/* Sample btn */
.im-samp {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px; border-radius: 7px;
  border: 1px solid ${T.border}; background: #fff;
  font-size: 12px; font-weight: 600; color: ${T.accentDark};
  cursor: pointer; transition: all .15s;
}
.im-samp:hover { background: ${T.accentBg}; border-color: ${T.accent}; }

/* Error text */
.im-err { font-size: 11.5px; color: #dc2626; margin-top: 4px; }

/* Bounce btn */
.im-bounce {
  width: 100%; padding: 9px; border: none; border-radius: 9px;
  background: #fef9c3; color: #854d0e;
  font-size: 12.5px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: background .15s;
}
.im-bounce:hover { background: #fef08a; }

/* Footer */
.im-ft {
  padding: 12px 22px;
  border-top: 1px solid ${T.border};
  display: flex; gap: 8px; flex-shrink: 0;
}
.im-btn {
  flex: 1; padding: 10px 0; border-radius: 10px;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  border: none; transition: all .15s;
}
.im-btn-cancel {
  background: ${T.surface}; color: ${T.sub};
  border: 1.5px solid ${T.border};
}
.im-btn-cancel:hover { background: #f1f5f9; }
.im-btn-submit { background: ${T.accent}; color: #fff; flex: 2; }
.im-btn-submit:hover:not(:disabled) { background: ${T.accentDark}; }
.im-btn-submit:disabled { opacity: .4; cursor: not-allowed; }

/* Success */
.im-ok {
  display: flex; flex-direction: column; align-items: center;
  padding: 48px 32px; text-align: center; gap: 6px;
}
.im-ok h2 { font-size: 20px; font-weight: 700; color: ${T.text}; margin-top: 14px; }
.im-ok p { font-size: 14px; color: ${T.muted}; max-width: 300px; }
.im-ok-btns { display: flex; gap: 10px; margin-top: 22px; }
`;


const ImportCustomers = ({ isAdd, isOpen, onClose }) => {
    const { t } = useTranslation();
    const [fileError, setFileError] = useState("")
    const dispatch = useDispatch();

    const [base64File, setBase64File] = useState("");
    const [fileName, setFileName] = useState("");
    const userData = useSelector((s) => s?.login?.user?.result);

    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState(false)
    const [successMessage, setSuccessMessage] = useState(false)
    const store_id = useSelector((s) => s?.login?.user?.result?.company_id);
    const user_id = useSelector((s) => s?.login?.user?.result?.user_id);
          const navigate = useNavigate();

    if (!isOpen) return null; // guard just in case

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["xls", "xlsx"].includes(ext)) {
            setFileError("Please upload a valid Excel file (.xls or .xlsx)");
            setBase64File(""); setFileName(""); return;
        }
        setFileError(""); setFileName(file.name);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => setBase64File(reader.result.split(",")[1]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault()

        const payload = {
            store_id,
            user_id,
            file_name: fileName,
            file: base64File
        }

        try {
            setLoading(true)
            const res = await axiosInstance.post(URLS.UPLOAD_BULK_CUSTOMER, payload)
            setLoading(false)
            const customerpayload = {
                offset: 0,
                limit: 10,
                store_id,
                search: "",
                type: "",
            };

            if (res.data.result.status_code === 200) {
                toast.success(t("Thank you. Your file has been accepted and is under process."));
                getCustomers(customerpayload)
                setSuccessMessage(res?.data.result?.status);
                setBase64File(""); setFileName("");
            }
            else {
                setErrorMessage(res.data.result.status || "An error occurred.");
            }
            console.log(res)
        }
        catch {
            console.log("Upload failed")
        }
    }

    const downloadSample = () => {
        const link = document.createElement("a");

        link.href = sampleFileCustomer;
        link.download = "sample_file_customer.xlsx";
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };



    return (
        <>
            <style>{css}</style>
            {loading && <LoadingSpinner/>}
            <div className="im im-ov" onClick={onClose}>
                {/* Stop clicks inside the modal from closing it */}
                <div className="im-box" onClick={(e) => e.stopPropagation()}>

                    {successMessage ?
                        (<div className="im-ok">
                            <CheckCircle2 size={52} color={T.accent} strokeWidth={1.5} />
                            <h2>{t("Upload Successful")}</h2>
                            <p>{t(successMessage)}</p>
                            <div className="im-ok-btns">
                                <button className="im-btn im-btn-cancel"
                                    style={{ padding: "10px 28px" }}
                                    onClick={() => { setSuccessMessage(""); onClose(); }}>
                                    {t("Close")}
                                </button>
                                <button className="im-btn im-btn-submit"
                                    style={{ padding: "10px 28px" }}
                                    onClick={() => { setSuccessMessage(""); onClose(); navigate("/settings/bulkupdatehistory",{state:{tab: "customer"}}); }}>
                                    {t("Check Status")}
                                </button>
                            </div>
                        </div>) :
                            (
                                <>
                                    <div className="im-hd">
                                        <span className="im-hd-title">{t("Import Customers")}</span>
                                        <button className="im-hd-close" onClick={onClose}>
                                            <CloseOutlined style={{ fontSize: 11 }} />
                                        </button>
                                    </div>

                                    {/* Body */}
                                    <div className="im-body">
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{ fontSize: 12.5, color: T.muted }}>{t("Need a template?")}</span>
                                            <button type="button" className="im-samp" onClick={() => downloadSample()}>
                                                <Download size={12} /> {t("Download Sample")}
                                            </button>
                                        </div>

                                        {/* Upload zone */}
                                        <div className="im-up">
                                            <div className="im-up-ico">
                                                <FileSpreadsheet size={17} color={T.accent} />
                                            </div>
                                            <div className="im-up-info">
                                                <div className="im-up-name">
                                                    {fileName ? fileName : t("No file chosen")}
                                                </div>
                                                <div className="im-up-sub">
                                                    {fileName ? t("Ready to submit") : t("Accepts .xls and .xlsx")}
                                                </div>
                                            </div>
                                            <input
                                                type="file"
                                                key={fileName}
                                                id="im-file"
                                                style={{ display: "none" }}
                                                accept=".xlsx,.xls"
                                                onChange={handleFile}
                                            />
                                            <button
                                                type="button"
                                                className="im-up-btn"
                                                onClick={() => document.getElementById("im-file").click()}
                                            >
                                                {t("Browse")}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="im-ft">
                                        <button className="im-btn im-btn-cancel" onClick={onClose}>
                                            {t("Cancel")}
                                        </button>
                                        <button
                                            className="im-btn im-btn-submit"
                                            onClick={handleSubmit}
                                            disabled={!base64File || loading}
                                        >
                                            {loading ? t("Importing...") : t("Import")}
                                        </button>
                                    </div>
                                </>)}
                </div>
            </div>
        </>
    );
};

export default ImportCustomers